// @flow

import memoize from 'memoize-one';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { selectCartUUID } from '@gandi/cart';

import { notifyDanger } from '@gandi/notifications';
import { contactbook } from '@gandi/redux-resource-contactbook';

import {
  getFormValues,
  withForm,
  SubmissionError,
  type FormValues,
} from 'gandi.react-form';

import { withLogger } from 'gandi.redux-logger';

import { withRouter } from 'src/helpers/withRouter';

import type { FullState, Dispatch, WithRouterProps } from 'src/types';

import { saveDraftContact } from '../../../modules/actions';
import { getSetupOverviewUrl } from '../../../modules/helpers';
import { selectSetupOwner } from '../../../modules/selectors';
import { contactValidation } from '../../modules/actions';

import Presenter from './Presenter';
import type { StateProps, Props, OwnProps } from './Presenter';

const EMPTY_OBJECT = {};
const FORM_ID = 'Create';

const getInitialValues = memoize((canWriteContactBook: boolean) => ({
  ...contactbook.defaultContactValue,
  saveToContactbook: canWriteContactBook,
}));

const selectFormValues = (state: FullState) =>
  getFormValues(FORM_ID)(state) || EMPTY_OBJECT;

const mapStateToProps = (
  state: FullState,
  { canWriteContactBook }: OwnProps,
): StateProps => {
  const orgId = selectSetupOwner(state);

  return {
    cartId: selectCartUUID(state),
    orgId,
    initialValues: getInitialValues(canWriteContactBook),
    formValues: selectFormValues(state),
    isWorking: contactbook.selectIsCreatingOne(state, orgId),
  };
};

export type FormProps = {
  ...Props,
  ...WithRouterProps<>,
};

export const onSubmit = async (
  formValues: FormValues,
  dispatch: Dispatch,
  props: FormProps,
) => {
  const { cartId, process, role, orgId } = props;

  const { saveToContactbook, ...contact } = formValues;
  if (formValues.type === 'individual') {
    delete contact.orgname;
  }

  if (saveToContactbook) {
    try {
      // Create new contact
      // $FlowFixMe[incompatible-indexer]
      await dispatch(contactbook.createContact(orgId, formValues)).then(
        response => {
          contact.id = response.data.id;
        },
      );
    } catch (err) {
      dispatch(
        notifyDanger({
          message: props.translator.__('Verify the information'),
        }),
      );

      throw new SubmissionError(err.errors);
    }
    dispatch(contactbook.invalidate());
  } else {
    try {
      // check validity of the contact
      // $FlowFixMe[incompatible-indexer]
      await dispatch(contactValidation({ contact, role }));
    } catch (err) {
      let errors;

      if (err.data?.errors) {
        errors = err.data.errors.reduce((acc, error) => {
          acc[error.name] = error.description;
          return acc;
        }, {});
      }

      dispatch(
        notifyDanger({
          message: props.translator.__('Verify the information'),
        }),
      );

      throw new SubmissionError(errors);
    }
  }

  dispatch(
    saveDraftContact({
      process,
      // $FlowFixMe[incompatible-indexer]
      contact,
      role,
    }),
  );

  const successUrl = getSetupOverviewUrl(cartId, process);
  props.navigate(successUrl);
};

// $FlowFixMe[signature-verification-failure]
const Create = compose(
  connect(mapStateToProps),
  withLogger('Batch/RoleContact/Create'),
  withRouter,
  withForm({
    form: FORM_ID,
    propNamespace: 'formProps',
    onSubmit,
  }),
)(Presenter);

Create.displayName = 'Create';

export default Create;
